import { z } from 'zod';
import { isAssertionKind } from './assertion-kinds';

/**
 * QP-1 — Quality Proof compose-on-read contract.
 * QP-2 — additive evidence content-digest integrity (verify-on-read).
 *
 * Reconstructable Proof DTO over existing canonical objects only.
 * Never a persistence table. Never sourced from ai_acceptance_evaluation.
 * Evidence refs are pointers; digests are SHA-256 of bytes when stamped.
 * claimsCryptographicBinding remains false (not sealed non-repudiation).
 */

export const QUALITY_PROOF_KINDS = ['execution-compose'] as const;
export type QualityProofKind = (typeof QUALITY_PROOF_KINDS)[number];

/** Existing evidence/artifact pointers only — never manufactured content. */
export const QUALITY_PROOF_EVIDENCE_REF_KINDS = ['screenshot', 'trace', 'artifact', 'video', 'file'] as const;
export type QualityProofEvidenceRefKind = (typeof QUALITY_PROOF_EVIDENCE_REF_KINDS)[number];

export const QualityProofEvidenceRefSchema = z.object({
  kind: z.enum(QUALITY_PROOF_EVIDENCE_REF_KINDS),
  /** URL, storage path, or existing evidence id — not a content hash binding claim. */
  ref: z.string().min(1).max(2_000),
});
export type QualityProofEvidenceRef = z.infer<typeof QualityProofEvidenceRefSchema>;

/**
 * Step status mirrors canonical sources (test_runs.step_results status or
 * quality_scenario_executions.step_results status). Keep open enough to
 * pass through without inventing a parallel vocabulary.
 */
export const QualityProofStepSchema = z.object({
  stepId: z.string().min(1).max(200),
  /** Present only when convert metadata / canvas metadata.scenarioStepId is explicit. */
  scenarioStepId: z.string().uuid().optional(),
  /** Present only when canvas fields.assertion is a known AssertionKind. */
  assertionKind: z.string().optional(),
  status: z.string().min(1).max(40),
  error: z.string().max(4_000).optional(),
  evidenceRefs: z.array(QualityProofEvidenceRefSchema).default([]),
});
export type QualityProofStep = z.infer<typeof QualityProofStepSchema>;

/** QP-2 — content integrity only; not sealed historical Proof. */
export const QUALITY_PROOF_INTEGRITY_STATUSES = ['verified', 'unverified'] as const;
export type QualityProofIntegrityStatus = (typeof QUALITY_PROOF_INTEGRITY_STATUSES)[number];

export const QualityProofEvidenceDigestSchema = z.object({
  runId: z.string().uuid(),
  stepId: z.string().min(1).max(200),
  kind: z.enum(QUALITY_PROOF_EVIDENCE_REF_KINDS),
  /** Existing evidence reference that was hashed (never a URL-string hash). */
  ref: z.string().min(1).max(2_000),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export type QualityProofEvidenceDigest = z.infer<typeof QualityProofEvidenceDigestSchema>;

export const QualityProofEvidenceIntegritySchema = z.object({
  algorithm: z.literal('sha256'),
  /**
   * verified = every stamped digest re-hashed successfully.
   * unverified = no stamped digests (legacy) — never claim URL hashes as content digests.
   * Integrity failures fail closed at the API (no `failed` success body).
   */
  status: z.enum(QUALITY_PROOF_INTEGRITY_STATUSES),
  digests: z.array(QualityProofEvidenceDigestSchema).default([]),
  /** Honesty: digests are tamper-evident content checks, not crypto sealing. */
  claimsSealedHistory: z.literal(false),
});
export type QualityProofEvidenceIntegrity = z.infer<typeof QualityProofEvidenceIntegritySchema>;

export const QualityProofSchema = z.object({
  proofKind: z.literal('execution-compose'),
  executionId: z.string().uuid(),
  scenarioId: z.string().uuid().nullable(),
  scenarioVersion: z.number().int().positive().nullable(),
  flowId: z.string().uuid().nullable(),
  /** Null for manual executions — never fabricated. */
  runId: z.string().uuid().nullable(),
  executionKind: z.enum(['manual', 'automation']),
  ranAgainst: z.string().max(80).nullable().optional(),
  /** Existing test_runs.trace_path when present — reference only, not Merkle binding. */
  traceRef: z.string().max(2_000).nullable().optional(),
  steps: z.array(QualityProofStepSchema),
  /**
   * Honesty lock: refs/digests are not immutable cryptographic evidence binding.
   */
  claimsCryptographicBinding: z.literal(false),
  /** QP-2 — content digest verify-on-read report (absent digests ⇒ unverified). */
  evidenceIntegrity: QualityProofEvidenceIntegritySchema.optional(),
});
export type QualityProof = z.infer<typeof QualityProofSchema>;

export const QualityProofResponseSchema = z.object({
  proof: QualityProofSchema,
});
export type QualityProofResponse = z.infer<typeof QualityProofResponseSchema>;

/** Safe assertion kind for Proof — never soft-maps unknown → visible. */
export function resolveProofAssertionKind(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  return isAssertionKind(trimmed) ? trimmed : undefined;
}
